import type { PayloadAction } from "@reduxjs/toolkit";
import type { currentSurveySliceState } from "@survey-types/survey.core.types";
import type { Property } from "@survey-types/survey.property.types";
import {
  addItemWithOrder,
  findAndUpdateById,
  removeByIdAndReorder,
  reorderArrayItems,
} from "@survey/utils/reducerFactory";

// =====================================
// SURVEY PROPERTIES MANAGEMENT
// =====================================

export const propertyReducers = {
  addProperty: (
    state: currentSurveySliceState,
    action: PayloadAction<Property>,
  ) => {
    if (state.currentSurvey?.properties) {
      state.currentSurvey.properties = addItemWithOrder(
        state.currentSurvey.properties,
        action.payload,
      );
    }
  },

  setProperty: (
    state: currentSurveySliceState,
    action: PayloadAction<Property>,
  ) => {
    if (!state.currentSurvey) {
      return;
    }

    if (!state.currentSurvey.properties) {
      state.currentSurvey.properties = [action.payload];
      return;
    }

    const existingIndex = state.currentSurvey.properties.findIndex(
      (property) => property.propertyId === action.payload.propertyId,
    );

    if (existingIndex !== -1) {
      // Update existing property
      state.currentSurvey.properties[existingIndex] = action.payload;
    } else {
      // Add new property
      state.currentSurvey.properties.push(action.payload);
    }
  },

  updateProperty: (
    state: currentSurveySliceState,
    action: PayloadAction<{
      propertyId: string;
      updates: Partial<Property>;
    }>,
  ) => {
    if (state.currentSurvey?.properties) {
      const { propertyId, updates } = action.payload;
      state.currentSurvey.properties = findAndUpdateById(
        state.currentSurvey.properties,
        propertyId,
        "propertyId",
        updates,
      );
    }
  },

  removeProperty: (
    state: currentSurveySliceState,
    action: PayloadAction<string>,
  ) => {
    if (state.currentSurvey?.properties) {
      state.currentSurvey.properties = removeByIdAndReorder(
        state.currentSurvey.properties,
        action.payload,
        "propertyId",
      );
    }
  },

  reorderProperties: (
    state: currentSurveySliceState,
    action: PayloadAction<{
      sourceIndex: number;
      destinationIndex: number;
    }>,
  ) => {
    if (state.currentSurvey?.properties) {
      const { sourceIndex, destinationIndex } = action.payload;
      state.currentSurvey.properties = reorderArrayItems(
        state.currentSurvey.properties,
        sourceIndex,
        destinationIndex,
      );
    }
  },

  reorderPropertyField: (
    state: currentSurveySliceState,
    action: PayloadAction<{
      sourceIndex: number;
      destinationIndex: number;
    }>,
  ) => {
    if (state.currentSurvey?.surveyPropertyFieldRule) {
      const { sourceIndex, destinationIndex } = action.payload;
      const pfr = state.currentSurvey.surveyPropertyFieldRule;
      const activeFields = pfr.activePropertyFieldRuleList;

      const [movedItem] = activeFields.splice(sourceIndex, 1);
      activeFields.splice(destinationIndex, 0, movedItem);
      activeFields.forEach((f, i) => {
        f.order = i;
      });

      const allUpdated = [
        ...pfr.activePropertyFieldRuleList,
        ...pfr.inactivePropertyFieldRuleList,
      ];
      pfr.propertyFieldRuleList.forEach((f) => {
        const updated = allUpdated.find((u) => u.fieldId === f.fieldId);
        if (updated) {
          f.order = updated.order;
        }
      });
    }
  },

  setPropertyFieldRules: (
    state: currentSurveySliceState,
    action: PayloadAction<{
      surveyId: string;
      propertyFieldRuleList: any[];
      activePropertyFieldRuleList: any[];
      inactivePropertyFieldRuleList: any[];
    }>,
  ) => {
    if (state.currentSurvey) {
      const {
        propertyFieldRuleList,
        activePropertyFieldRuleList,
        inactivePropertyFieldRuleList,
      } = action.payload;
      state.currentSurvey.surveyPropertyFieldRule = {
        surveyId: state.currentSurvey.id,
        propertyFieldRuleList: propertyFieldRuleList ?? [],
        activePropertyFieldRuleList,
        inactivePropertyFieldRuleList,
      };
    }
  },

  updatePropertyFieldRule: (
    state: currentSurveySliceState,
    action: PayloadAction<{
      fieldId: string;
      isHidden: boolean;
      status: "ACTIVE" | "INACTIVE";
      order: number;
    }>,
  ) => {
    if (state.currentSurvey?.surveyPropertyFieldRule) {
      const { fieldId, isHidden, status, order } = action.payload;
      const pfr = state.currentSurvey.surveyPropertyFieldRule;
      const field = pfr.propertyFieldRuleList.find(
        (field) => field.fieldId === fieldId,
      );
      if (field) {
        field.isHidden = isHidden;
        field.status = status;
        field.order = order;
      }

      const ADDRESS_FIELD_ORDER = [
        "name",
        "address1",
        "city",
        "stateProvince",
        "postalCode",
      ];

      if (isHidden) {
        const fieldToMove = pfr.activePropertyFieldRuleList.find(
          (f) => f.fieldId === fieldId,
        );
        if (fieldToMove) {
          fieldToMove.isHidden = isHidden;
          fieldToMove.order = order;
          fieldToMove.status = status;
          pfr.inactivePropertyFieldRuleList.push(fieldToMove);
          pfr.activePropertyFieldRuleList =
            pfr.activePropertyFieldRuleList.filter(
              (f) => f.fieldId !== fieldId,
            );
          pfr.activePropertyFieldRuleList.forEach((f, i) => {
            f.order = i;
          });
          pfr.inactivePropertyFieldRuleList.sort((a, b) => {
            if (a.order !== b.order) return a.order - b.order;
            const ai = ADDRESS_FIELD_ORDER.indexOf(a.fieldKey);
            const bi = ADDRESS_FIELD_ORDER.indexOf(b.fieldKey);
            if (ai !== -1 && bi !== -1) return ai - bi;
            return 0;
          });
          pfr.inactivePropertyFieldRuleList.forEach((f, i) => {
            f.order = i;
          });
        }
      } else {
        const fieldToMove = pfr.inactivePropertyFieldRuleList.find(
          (f) => f.fieldId === fieldId,
        );
        if (fieldToMove) {
          fieldToMove.isHidden = isHidden;
          fieldToMove.order = order;
          fieldToMove.status = status;
          pfr.activePropertyFieldRuleList.push(fieldToMove);
          pfr.inactivePropertyFieldRuleList =
            pfr.inactivePropertyFieldRuleList.filter(
              (f) => f.fieldId !== fieldId,
            );
          pfr.activePropertyFieldRuleList.sort((a, b) => {
            if (a.order !== b.order) return a.order - b.order;
            const ai = ADDRESS_FIELD_ORDER.indexOf(a.fieldKey);
            const bi = ADDRESS_FIELD_ORDER.indexOf(b.fieldKey);
            if (ai !== -1 && bi !== -1) return ai - bi;
            return 0;
          });
          pfr.activePropertyFieldRuleList.forEach((f, i) => {
            f.order = i;
          });
          pfr.inactivePropertyFieldRuleList.forEach((f, i) => {
            f.order = i;
          });
        }
      }

      pfr.propertyFieldRuleList.forEach((f) => {
        const updated = [
          ...pfr.activePropertyFieldRuleList,
          ...pfr.inactivePropertyFieldRuleList,
        ].find((u) => u.fieldId === f.fieldId);
        if (updated) {
          f.order = updated.order;
        }
      });
    }
  },

  updatePropertyFieldRulesBulk: (
    state: currentSurveySliceState,
    action: PayloadAction<
      {
        fieldId: string;
        isHidden: boolean;
        status: "ACTIVE" | "INACTIVE";
        order?: number;
      }[]
    >,
  ) => {
    if (state.currentSurvey?.surveyPropertyFieldRule) {
      const updates = action.payload;
      const pfr = state.currentSurvey.surveyPropertyFieldRule;
      const fields = pfr.propertyFieldRuleList;

      updates.forEach((update) => {
        const field = fields.find((f) => f.fieldId === update.fieldId);
        if (field) {
          field.isHidden = update.isHidden;
          field.status = update.status;
          if (update.order !== undefined) {
            field.order = update.order;
          }
        }
      });

      pfr.activePropertyFieldRuleList = fields
        .filter((f) => !f.isHidden)
        .sort((a, b) => a.order - b.order);
      pfr.inactivePropertyFieldRuleList = fields
        .filter((f) => f.isHidden)
        .sort((a, b) => a.order - b.order);

      pfr.activePropertyFieldRuleList.forEach((f, i) => {
        f.order = i;
      });
      pfr.inactivePropertyFieldRuleList.forEach((f, i) => {
        f.order = i;
      });
      fields.forEach((f) => {
        const updated = [
          ...pfr.activePropertyFieldRuleList,
          ...pfr.inactivePropertyFieldRuleList,
        ].find((u) => u.fieldId === f.fieldId);
        if (updated) {
          f.order = updated.order;
        }
      });
    }
  },

  currentPropertyId: (
    state: currentSurveySliceState,
    action: PayloadAction<{ propertyId: string | null } | null>,
  ) => {
    state.currentPropertyId = action.payload ? action.payload.propertyId : null;
  },
};
